import { Splide, SplideSlide } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import React, { Fragment, useMemo, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';

import ResponsiveImage from 'components/ResponsiveImage';
import VideoPlayer from 'components/VideoPlayer';
import { IMAGE_WIDTHS } from 'constants/images';
import { mediumUp } from 'constants/mediaQueries';
import { breakpoints, cn } from 'utils/Tailwind';

import '@splidejs/react-splide/css';

const CAROUSEL_OPTIONS = {
  type: 'loop',
  pagination: false,
  perPage: 1,
  arrows: false,
  gap: '8px',
  mediaQuery: 'min',
  waitForTransition: true,
  updateOnMove: true,
  breakpoints: {
    0: {
      padding: {
        left: '0',
        right: '30vw',
      },
    },
    [breakpoints.sm]: {
      padding: {
        left: '0',
        right: '60vw',
      },
    },
  },
};

function PageSectionShoppableFeaturedCollection({
  asset,
  assetMobile,
  ctaLinks,
  isInverted,
  items,
  subtitle,
  title,
  trackCTAClick,
}) {
  const isMediumUp = useMediaQuery({ query: mediumUp });
  const videoUrl = isMediumUp ? asset.file.url : assetMobile.file.url;
  const videoRatio = isMediumUp ? '1:1' : '4:5';

  return (
    <section className="flex flex-col gap-2 md:flex-row lg:m-10">
      <ShoppableFeaturedHero
        asset={asset}
        assetMobile={assetMobile}
        ctaLinks={ctaLinks}
        isInverted={isInverted}
        isMediumUp={isMediumUp}
        subtitle={subtitle}
        title={title}
        trackCTAClick={trackCTAClick}
        videoRatio={videoRatio}
        videoUrl={videoUrl}
      />
      <MobileCarousel items={items} trackCTAClick={trackCTAClick} />
      <DesktopCarousel items={items} trackCTAClick={trackCTAClick} />
    </section>
  );
}

function ShoppableFeaturedHero({
  asset,
  assetMobile,
  ctaLinks,
  isInverted,
  isMediumUp,
  subtitle,
  title,
  trackCTAClick,
  videoRatio,
  videoUrl,
}) {
  return (
    <div className="group/hero relative aspect-4/5 overflow-hidden md:aspect-square md:w-1/2">
      {(isMediumUp && asset.file.contentType.includes('video')) ||
      (!isMediumUp && assetMobile.file.contentType.includes('video')) ? (
        <VideoPlayer
          className="absolute inset-0"
          url={videoUrl}
          playing={true}
          aspectRatio={videoRatio}
          muted
          loop
          playsinline
        />
      ) : (
        <ResponsiveImage
          className="h-full w-full object-cover transition-transform duration-300 ease-in-out lg:group-hover/hero:scale-105"
          desktopWidth={IMAGE_WIDTHS.W_2000}
          desktopHeight={IMAGE_WIDTHS.W_2000}
          mobileWidth={IMAGE_WIDTHS.W_2000}
          mobileHeight={IMAGE_WIDTHS.W_2000}
          desktopSrc={asset.file.url}
          mobileSrc={assetMobile.file.url}
          alt={asset.description}
          coverParentNode
          lazy
        />
      )}

      <HeroContent
        ctaLinks={ctaLinks}
        isInverted={isInverted}
        subtitle={subtitle}
        title={title}
        trackCTAClick={trackCTAClick}
      />
    </div>
  );
}

function HeroContent({ ctaLinks, isInverted, subtitle, title, trackCTAClick }) {
  return (
    <div
      data-testid="hero-content"
      className={cn('absolute inset-0 flex flex-col justify-end p-6 lg:p-10', {
        'text-white': isInverted,
      })}
    >
      {title && <h2 className="mb-2 text-1xl font-bold lg:text-2xl xl:text-3xl">{title}</h2>}
      {subtitle && <p className="text-sm lg:text-base">{subtitle}</p>}

      <div className="mt-4 flex gap-2 md:gap-4 xl:mt-8">
        {ctaLinks.map(({ text, url, openInNewWindow }) => (
          <a
            key={url}
            href={url}
            className={cn(
              `w-fit flex-1 whitespace-pre border bg-transparent bg-white p-3.5
              text-center text-xs font-medium uppercase tracking-widest text-black 
              transition-colors duration-300 ease-in-out
              hover:bg-black hover:text-white md:min-w-40 md:flex-none`,
              [!isInverted && 'text-black']
            )}
            onClick={() => trackCTAClick({ url, text })}
            {...(openInNewWindow && { target: '_blank' })}
          >
            {text}
          </a>
        ))}
      </div>
    </div>
  );
}

function MobileCarousel({ items, trackCTAClick }) {
  const splideRef = useRef(null);
  const transformedItems = useMemo(() => transformMobileShoppableItems(items), [items]);

  return (
    <Splide
      data-testid="shoppable-carousel-mobile"
      options={CAROUSEL_OPTIONS}
      className="md:hidden"
      ref={splideRef}
    >
      {transformedItems.map(item => (
        <SplideSlide key={item.handle}>
          <a
            data-testid="colorway-card-single"
            href={`/products/${item.handle}`}
            className="block aspect-square h-full w-full bg-grey"
            onClick={() =>
              trackCTAClick({ url: `/products/${item.handle}`, text: item.masterName })
            }
          >
            <CardContent {...item} />
          </a>
        </SplideSlide>
      ))}
    </Splide>
  );
}

function DesktopCarousel({ items, trackCTAClick }) {
  return (
    <div
      data-testid="shoppable-cards-desktop"
      className="hidden grid-cols-1 gap-2 md:grid md:w-1/2 md:grid-cols-2 md:grid-rows-2"
    >
      {items.map(item => (
        <Fragment key={item.handle}>
          {item.ctaLinks?.length > 0 ? (
            <div
              data-testid="colorway-card-genders"
              className="group/card relative overflow-hidden bg-grey"
            >
              <CardContent {...item} />
              <CardCTAs ctaLinks={item.ctaLinks} trackCTAClick={trackCTAClick} />
            </div>
          ) : (
            <a
              data-testid="colorway-card-single"
              href={`/products/${item.handle}`}
              className="group/card relative overflow-hidden bg-grey"
              onClick={() =>
                trackCTAClick({ url: `/products/${item.handle}`, text: item.masterName })
              }
            >
              <CardContent {...item} />
            </a>
          )}
        </Fragment>
      ))}
    </div>
  );
}

function CardContent({
  image,
  colorName,
  colorNameNoSole,
  compareAtPrice,
  masterName,
  masterNameNoGender,
  price,
}) {
  return (
    <>
      <ResponsiveImage
        className="-translate-y-8 scale-90 transition-transform duration-300 ease-in-out md:-translate-y-4 md:group-hover/card:scale-105"
        src={image.src}
        width={IMAGE_WIDTHS.W_600}
        height={IMAGE_WIDTHS.W_600}
        lazy
      />
      <div className="absolute inset-0 hidden flex-col justify-end p-4 text-black md:flex">
        <div className="mb-1 flex items-center justify-between text-sm leading-tight lg:mb-2">
          <p className="text-sm font-medium">{masterNameNoGender || masterName}</p>
          <p className="ml-2 text-xs">
            <span className={compareAtPrice && 'text-discount-red'}>{price}</span>
            {compareAtPrice && <span className="ml-1 line-through">{compareAtPrice}</span>}
          </p>
        </div>
        <p className="text-xs/none">{colorNameNoSole || colorName}</p>
      </div>
      <div className="absolute inset-0 flex flex-col justify-end gap-1 p-4 text-black md:hidden">
        <p className="text-sm font-medium">{masterNameNoGender || masterName}</p>
        <p className="text-xs/none sm:text-sm/none md:text-xs/none lg:text-sm/none">
          {colorNameNoSole || colorName}
        </p>
        <p className="text-xs">
          <span className={compareAtPrice && 'text-discount-red'}>{price}</span>
          {compareAtPrice && <span className="ml-1 line-through">{compareAtPrice}</span>}
        </p>
      </div>
    </>
  );
}

function CardCTAs({ ctaLinks, trackCTAClick }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black bg-transparent opacity-0 transition-all duration-500 ease-in-out focus-within:bg-black focus-within:bg-opacity-40 focus-within:opacity-100 group-hover/card:bg-black group-hover/card:bg-opacity-40 group-hover/card:opacity-100">
      <div className="flex w-full flex-col gap-2 px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
        {ctaLinks.map(({ text, url, openInNewWindow }) => (
          <a
            data-testid="card-ctalink-desktop"
            key={url}
            href={url}
            onClick={() => trackCTAClick({ url, text })}
            className="w-full whitespace-pre border bg-transparent bg-white p-3 text-center text-xs font-medium uppercase tracking-widest text-black transition-all duration-300 ease-in-out hover:bg-black hover:text-white"
            {...(openInNewWindow && { target: '_blank' })}
          >
            {text}
          </a>
        ))}
      </div>
    </div>
  );
}

/* 
  Transform items to create a single array of items with the ctaLinks expanded
  
  If an item has ctaLinks, create a new item for each ctaLink using the url from the ctaLink as the handle
  and the masterName from the ctaLink as the masterName
*/
export function transformMobileShoppableItems(items) {
  return items.flatMap(item => {
    if (item?.ctaLinks?.length > 0) {
      return item.ctaLinks.map(ctaLink => {
        const urlPath = ctaLink.url.replace('/products/', '');
        let ctaLinkGender = '';

        if (urlPath.startsWith('womens-')) {
          ctaLinkGender = "Women's ";
        } else if (urlPath.startsWith('mens-')) {
          ctaLinkGender = "Men's ";
        }

        const newMasterName = `${ctaLinkGender}${item.masterNameNoGender}`;

        return {
          ...item,
          handle: urlPath,
          masterName: newMasterName,
          masterNameNoGender: newMasterName,
        };
      });
    }
    return item;
  });
}

PageSectionShoppableFeaturedCollection.propTypes = {
  asset: PropTypes.shape({
    description: PropTypes.string,
    file: PropTypes.shape({
      url: PropTypes.string,
      contentType: PropTypes.string,
    }),
  }),
  assetMobile: PropTypes.shape({
    description: PropTypes.string,
    file: PropTypes.shape({
      url: PropTypes.string,
      contentType: PropTypes.string,
    }),
  }),
  ctaLinks: PropTypes.array,
  isInverted: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  trackCTAClick: PropTypes.func,
};

PageSectionShoppableFeaturedCollection.defaultProps = {
  asset: { file: { contentType: '', url: '' } },
  assetMobile: { file: { contentType: '', url: '' } },
  ctaLinks: [],
  isInverted: false,
  items: [],
  subtitle: '',
  title: '',
};

ShoppableFeaturedHero.propTypes = {
  asset: PropTypes.shape({
    description: PropTypes.any,
    file: PropTypes.shape({
      contentType: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  assetMobile: PropTypes.shape({
    file: PropTypes.shape({
      contentType: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  ctaLinks: PropTypes.array,
  isInverted: PropTypes.bool,
  isMediumUp: PropTypes.bool,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  trackCTAClick: PropTypes.func,
  videoRatio: PropTypes.string,
  videoUrl: PropTypes.string,
};

ShoppableFeaturedHero.defaultProps = {
  asset: { file: { contentType: '', url: '' } },
  assetMobile: { file: { contentType: '', url: '' } },
  ctaLinks: [],
  isInverted: false,
  isMediumUp: false,
  subtitle: '',
  title: '',
  videoRatio: '1:1',
  videoUrl: '',
};

HeroContent.propTypes = {
  ctaLinks: PropTypes.array,
  isInverted: PropTypes.bool,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  trackCTAClick: PropTypes.func,
};

HeroContent.defaultProps = {
  ctaLinks: [],
  isInverted: false,
  subtitle: '',
  title: '',
};
DesktopCarousel.propTypes = {
  items: PropTypes.array,
  trackCTAClick: PropTypes.func,
};

MobileCarousel.propTypes = {
  items: PropTypes.array,
  trackCTAClick: PropTypes.func,
};

CardCTAs.propTypes = {
  ctaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      url: PropTypes.string,
      openInNewWindow: PropTypes.bool,
    })
  ),
  trackCTAClick: PropTypes.func,
};

CardCTAs.defaultProps = {
  ctaLinks: [],
};

CardContent.propTypes = {
  colorName: PropTypes.string,
  colorNameNoSole: PropTypes.string,
  compareAtPrice: PropTypes.string,
  handle: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string,
  }),
  masterName: PropTypes.string,
  masterNameNoGender: PropTypes.string,
  price: PropTypes.string,
  trackCTAClick: PropTypes.func,
};

export default PageSectionShoppableFeaturedCollection;
